import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { currency } from "@/components/Primitives";
import { ArrowLeft, CheckCircle2, Circle, Star, Plus, Save, FileText, Upload, ExternalLink, Loader2, History, Mail, ChevronDown, Send, Bell, Pencil, RotateCcw, Trash2, X, CalendarDays, Home } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { CalendarInviteButton, CalendarInviteDialog, InviteHistory, inviteOutdated } from "@/components/CalendarInviteDialog";

const KEY_DATES = [
  ["contract_date", "Contract Date"], ["earnest_money_deadline", "Earnest Money"],
  ["closing_date", "Closing Date"], ["inspection_deadline", "Inspection"],
  ["financing_deadline", "Financing"], ["appraisal_deadline", "Appraisal"],
  ["insurance_deadline", "Insurance"], ["possession_date", "Possession"],
];
const DATE_SHORT = {
  contract_date: "Contract", earnest_money_deadline: "EM Due Date", closing_date: "Closing",
  inspection_deadline: "Inspection", financing_deadline: "Financing", appraisal_deadline: "Appraisal",
  insurance_deadline: "Insurance", possession_date: "Possession",
};
const PARTY_GROUPS = [
  ["Assigned Coordinator", "coordinator"],
  ["Buyer", "buyer"], ["Buyer 2", "buyer2"], ["Seller", "seller"], ["Seller 2", "seller2"],
  ["Buyer's Agent", "buyer_agent"],
  ["Seller's Agent", "seller_agent"], ["Agent Assistant", "agent_assistant"], ["Lender", "lender"], ["Loan Processor", "loan_processor"], ["Title Company", "title_company"], ["Home Warranty", "home_warranty"],
];
const ALL_FIELDS = [
  ...KEY_DATES.map(([k]) => k), "purchase_price", "earnest_money", "notes", "expected_close_days",
  "buyer_name", "buyer_email", "buyer_phone", "seller_name", "seller_email", "seller_phone",
  "buyer2_name", "buyer2_email", "buyer2_phone", "seller2_name", "seller2_email", "seller2_phone",
  "coordinator_name", "coordinator_email", "coordinator_phone",
  "buyer_agent_name", "buyer_agent_email", "buyer_agent_phone",
  "seller_agent_name", "seller_agent_email", "seller_agent_phone",
  "agent_assistant_name", "agent_assistant_email", "agent_assistant_phone",
  "loan_processor_name", "loan_processor_email", "loan_processor_phone",
  "lender_name", "lender_email", "lender_phone", "title_company_name", "title_company_email", "title_company_phone",
  "home_warranty_name", "home_warranty_email", "home_warranty_phone",
];
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.1em] uppercase text-[#8a8a86] block mb-1";
const daysTo = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

function MiniProgress({ filled, count, pct, testid }) {
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid={testid}>
      <div className="w-24 h-2 rounded-full bg-[#F0EFEC] overflow-hidden">
        <div className="h-full bg-[#1F8A4C] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-[#575755] whitespace-nowrap">{filled}/{count}</span>
    </div>
  );
}

function Card({ title, action, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E5E2] rounded-2xl shadow-sm">
      <div className="px-6 pt-5 pb-4 flex items-center justify-between gap-3">
        <h3 className="font-serif text-2xl text-[#0A0A0A]">{title}</h3>{action}
      </div>
      <div className="px-6 pb-6 border-t border-[#F0EFEC] pt-5">{children}</div>
    </div>
  );
}

function DateField({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  const year = new Date().getFullYear();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={`${inp} flex items-center justify-between text-left`}>
          <span className={date ? "text-[#0A0A0A]" : "text-[#a3a099]"}>{date ? format(date, "MMM d, yyyy") : "Select date"}</span>
          <CalendarDays className="w-4 h-4 text-[#8a8a86] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
        <Calendar mode="single" selected={date} defaultMonth={date}
          captionLayout="dropdown-buttons" fromYear={year - 3} toYear={year + 6}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}
function buildNotifyBody(txn) {
  const rows = KEY_DATES.filter(([k]) => txn[k]).map(([k, label]) =>
    `<tr><td style="padding:6px 0;font-weight:600;color:#0A0A0A">${DATE_SHORT[k] || label}</td><td style="padding:6px 0;text-align:right;color:#575755">${(txn[k] || "").slice(0, 10)}</td></tr>`).join("");
  return `<h3 style="color:#1F3C88;margin:0 0 8px">Transaction Update — Key Dates</h3><p style="margin:0 0 12px">Here are the current key dates for:</p><div style="border:1px solid #E5E5E2;border-radius:10px;padding:16px"><p style="margin:0 0 12px"><strong>Property:</strong> ${txn.property_address || ""}</p><table style="width:100%;border-collapse:collapse">${rows}</table></div>`;
}
const fmtDate = (d) => {
  const s = (d || "").slice(0, 10);
  if (!s) return "—";
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return "—";
  return `${m}/${day}/${y.slice(2)}`;
};

const TYPE_BY_BASE = { coordinator: "vendor", buyer: "client", buyer2: "client", seller: "client", seller2: "client", buyer_agent: "agent", seller_agent: "agent", agent_assistant: "vendor", lender: "lender", loan_processor: "lender", title_company: "title", home_warranty: "vendor" };

const splitEmails = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const QUICK_PICK = ["Buyer", "Buyer 2", "Seller", "Seller 2", "Lender"];

function QuickPickChips({ recipients, value, onChange, testid }) {
  const picks = (recipients || []).filter((r) => QUICK_PICK.includes(r.label));
  if (!picks.length) return null;
  const current = splitEmails(value);
  const toggle = (email) => onChange((current.includes(email) ? current.filter((e) => e !== email) : [...current, email]).join(", "));
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`${testid}-chips`}>
      {picks.map((r) => {
        const on = current.includes(r.email);
        return (
          <button type="button" key={r.label} title={r.email} data-testid={`${testid}-chip-${r.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle(r.email)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors duration-150 ${on ? "bg-[#1F3C88] border-[#1F3C88] text-white" : "bg-white border-[#E5E5E2] text-[#1F3C88] hover:border-[#1F3C88]"}`}>
            {on ? "✓ " : "+ "}{r.label}
          </button>
        );
      })}
    </div>
  );
}
const fmtSentAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function PartyField({ base, label, form, set, people, onCreatePerson }) {
  const [openList, setOpenList] = useState(false);
  const name = form[`${base}_name`] || "";
  const q = name.trim().toLowerCase();
  const matches = q.length >= 1
    ? people.filter((p) => `${p.name} ${p.company || ""} ${p.email || ""}`.toLowerCase().includes(q) && p.name !== name).slice(0, 6)
    : [];
  const exact = people.some((p) => (p.name || "").toLowerCase() === q);
  const canQuickAdd = q.length >= 2 && !exact;
  const choose = (p) => {
    set(`${base}_name`, p.name || "");
    set(`${base}_email`, p.email || "");
    set(`${base}_phone`, p.phone || "");
    setOpenList(false);
  };
  const quickAdd = async () => {
    await onCreatePerson({ name: name.trim(), type: TYPE_BY_BASE[base] || "client", email: form[`${base}_email`] || "", phone: form[`${base}_phone`] || "" });
    setOpenList(false);
  };
  return (
    <div className="pb-3 border-b border-[#F5F4F1] last:border-0 last:pb-0">
      <div className="text-xs font-semibold text-[#1F3C88] mb-1.5">{label}</div>
      <div className="relative">
        <input data-testid={`field-${base}_name`} className={`${inp} mb-1.5`} placeholder="Start typing a name…" autoComplete="off"
          value={name}
          onChange={(e) => { set(`${base}_name`, e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 180)} />
        {openList && (matches.length > 0 || canQuickAdd) && (
          <div data-testid={`autocomplete-${base}`} className="absolute z-30 left-0 right-0 top-full -mt-1 bg-white border border-[#E5E5E2] rounded-lg shadow-lg max-h-56 overflow-auto">
            {matches.map((p) => (
              <button type="button" key={p.id} data-testid={`autocomplete-option-${base}-${p.id}`}
                onMouseDown={(e) => e.preventDefault()} onClick={() => choose(p)}
                className="w-full text-left px-3 py-2 hover:bg-[#F0EFEC] transition-colors border-b border-[#F5F4F1] last:border-0">
                <div className="text-sm text-[#0A0A0A] font-medium">{p.name}</div>
                <div className="text-xs text-[#8a8a86]">{[p.company, p.email, p.phone].filter(Boolean).join(" · ") || "No contact info"}</div>
              </button>
            ))}
            {canQuickAdd && (
              <button type="button" data-testid={`quick-add-${base}`} onMouseDown={(e) => e.preventDefault()} onClick={quickAdd}
                className="w-full text-left px-3 py-2.5 flex items-center gap-1.5 text-sm font-semibold text-[#1F3C88] bg-[#F7FAFF] hover:bg-[#EAF0FB] transition-colors">
                <Plus className="w-4 h-4" /> Add "{name.trim()}" to People
              </button>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input data-testid={`field-${base}_email`} className={inp} placeholder="Email" value={form[`${base}_email`] || ""} onChange={(e) => set(`${base}_email`, e.target.value)} />
        <input data-testid={`field-${base}_phone`} className={inp} placeholder="Phone" value={form[`${base}_phone`] || ""} onChange={(e) => set(`${base}_phone`, e.target.value)} />
      </div>
    </div>
  );
}

function EmailField({ value, onChange, options, testid }) {
  const listId = `${testid}-options`;
  return (
    <div>
      <input data-testid={testid} list={listId} value={value} onChange={(e) => onChange(e.target.value)} placeholder="name@email.com"
        className="w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none" />
      <datalist id={listId}>{(options || []).map((r) => <option key={r.email} value={r.email}>{r.label}</option>)}</datalist>
    </div>
  );
}

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = (user?.tools || []).includes("transactions") || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [txn, setTxn] = useState(null);
  const [form, setForm] = useState({});
  const [newTask, setNewTask] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [people, setPeople] = useState([]);
  const [applyId, setApplyId] = useState("");
  const [openTask, setOpenTask] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [inviteKey, setInviteKey] = useState(null);
  const [notifyTo, setNotifyTo] = useState([]);
  const [notifyInput, setNotifyInput] = useState("");
  const [notifySubject, setNotifySubject] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyEditBody, setNotifyEditBody] = useState(false);
  const [listingUrl, setListingUrl] = useState("");
  const [fetchingListing, setFetchingListing] = useState(false);
  const [managerEmail, setManagerEmail] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const createPerson = async (payload) => {
    try {
      await api.post("/people", payload);
      const { data } = await api.get("/people");
      setPeople(data);
      toast.success(`${payload.name} added to People`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/tm/workflows").then(({ data }) => setWorkflows(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/people").then(({ data }) => setPeople(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/tm/config").then(({ data }) => setManagerEmail(data?.manager_email || "")).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tm/transactions/${id}`);
    setTxn(data);
    const f = {};
    ALL_FIELDS.forEach((k) => { f[k] = KEY_DATES.some(([d]) => d === k) ? (data[k] || "").slice(0, 10) : (data[k] ?? ""); });
    setForm(f);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (txn?.listing_url) setListingUrl(txn.listing_url); }, [txn?.listing_url]);

  if (!txn) return null;

  const saveLink = async () => {
    if (!listingUrl.trim().startsWith("http")) { toast.error("Paste a listing URL first"); return; }
    try { await api.put(`/tm/transactions/${id}`, { listing_url: listingUrl.trim() }); toast.success("Listing link saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const fetchListing = async () => {
    if (!listingUrl.trim().startsWith("http")) { toast.error("Paste a listing URL first"); return; }
    setFetchingListing(true);
    try {
      const { data: started } = await api.post(`/tm/scrape-listing`, { url: listingUrl.trim() });
      const runId = started.run_id;
      const source = started.source || "zillow";
      let tries = 0;
      let result = null;
      while (tries < 45) {
        await new Promise((res) => setTimeout(res, 3000));
        const { data: s } = await api.get(`/tm/scrape-listing/${runId}`, { params: { source } });
        if (s.status === "running") { tries++; continue; }
        result = s;
        break;
      }
      if (!result) { toast.error("Couldn't find an active listing for that link — it may be off-market. Enter the details manually."); return; }
      if (result.status !== "done") { toast.error(result.detail || "No listing data found"); return; }
      await api.put(`/tm/transactions/${id}`, {
        property_address: result.property_address || txn.property_address,
        purchase_price: result.purchase_price ?? txn.purchase_price,
        listing_url: listingUrl.trim(),
        hero_photo: result.hero_photo || txn.hero_photo || "",
        beds: result.beds ?? txn.beds ?? null,
        baths: result.baths ?? txn.baths ?? null,
        sqft: result.sqft ?? txn.sqft ?? null,
      });
      toast.success("Listing details imported");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFetchingListing(false); }
  };

  const save = async () => {
    const payload = { ...form };
    payload.purchase_price = payload.purchase_price === "" ? null : Number(payload.purchase_price);
    payload.earnest_money = payload.earnest_money === "" ? null : Number(payload.earnest_money);
    const datesChanged = KEY_DATES.some(([k]) => (form[k] || "") !== (txn[k] || "").slice(0, 10));
    try { await api.put(`/tm/transactions/${id}`, payload); toast.success(datesChanged ? "Saved — deadlines recalculated" : "Saved"); setEditing(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleTask = async (t) => {
    try { await api.put(`/tm/transactions/${id}/tasks/${t.task_id}`, { status: t.status === "completed" ? "pending" : "completed" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    try { await api.post(`/tm/transactions/${id}/tasks`, { title: newTask }); setNewTask(""); toast.success("Task added"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    try { await api.put(`/tm/transactions/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/tm/transactions/${id}/documents`, fd); toast.success("Document attached"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingDoc(false); }
  };
  const applyWorkflow = async (mode) => {
    if (!applyId) { toast.error("Choose a workflow first"); return; }
    const wf = workflows.find((w) => w.template_id === applyId);
    const msg = mode === "replace"
      ? `Replace all current tasks with the "${wf?.name}" workflow? This clears existing checklist items.`
      : `Add the "${wf?.name}" workflow tasks on top of the current checklist?`;
    if (!window.confirm(msg)) return;
    try { await api.post(`/tm/transactions/${id}/apply-workflow`, { template_id: applyId, mode }); toast.success("Workflow applied"); setApplyId(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveTaskContent = async (t, patch) => {
    try { await api.put(`/tm/transactions/${id}/tasks/${t.task_id}`, patch); toast.success("Task updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendTaskEmail = async (t, payload) => {
    const data0 = typeof payload === "string" ? { to: payload } : (payload || {});
    if (!data0.to) { toast.error("Add a 'To' recipient"); return; }
    try { const { data } = await api.post(`/tm/transactions/${id}/tasks/${t.task_id}/send`, data0); toast.success(`Email sent to ${data.sent_to}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resetTaskEmail = async (t) => {
    try { await api.post(`/tm/transactions/${id}/tasks/${t.task_id}/reset-email`); toast.success("Email reset to template wording"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteTxn = async () => {
    if (!window.confirm("Delete this transaction? This can't be undone.")) return;
    try { await api.delete(`/tm/transactions/${id}`); toast.success("Transaction deleted"); navigate("/transactions"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const notifyParties = () => {
    const emails = [txn.coordinator_email, txn.buyer_email, txn.buyer2_email, txn.seller_email, txn.seller2_email, txn.buyer_agent_email, txn.seller_agent_email, txn.agent_assistant_email, txn.lender_email, txn.loan_processor_email, txn.title_company_email, txn.home_warranty_email].filter(Boolean);
    setNotifyTo([...new Set(emails)]);
    setNotifySubject(`Transaction Update: ${txn.property_address}`);
    setNotifyBody(buildNotifyBody(txn));
    setNotifyInput(""); setNotifyEditBody(false); setNotifyOpen(true);
  };
  const commitRecipient = () => {
    const v = notifyInput.trim().replace(/,+$/, "").trim();
    if (v && !notifyTo.includes(v)) setNotifyTo((r) => [...r, v]);
    setNotifyInput("");
  };
  const addRecipient = (e) => {
    if (e.key && e.key !== "Enter" && e.key !== ",") return;
    if (e.key) e.preventDefault();
    commitRecipient();
  };
  const sendNotify = async () => {
    if (notifyTo.length === 0) { toast.error("Add at least one recipient"); return; }
    try {
      const { data } = await api.post(`/tm/transactions/${id}/notify-parties`, { recipients: notifyTo, subject: notifySubject, html: notifyBody });
      toast.success(data.sent ? `Notification sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}` : "No recipients");
      setNotifyOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const recipientOptions = () => {
    const opts = [
      ["buyer_email", "Buyer"], ["buyer2_email", "Buyer 2"], ["seller_email", "Seller"], ["seller2_email", "Seller 2"],
      ["buyer_agent_email", "Buyer Agent"], ["seller_agent_email", "Seller Agent"], ["agent_assistant_email", "Agent Assistant"],
      ["lender_email", "Lender"], ["loan_processor_email", "Loan Processor"], ["title_company_email", "Title Co."], ["home_warranty_email", "Home Warranty"], ["coordinator_email", "Coordinator"],
    ];
    return opts.filter(([k]) => txn[k]).map(([k, label]) => ({ email: txn[k], label }));
  };

  const saveNotes = async () => {
    try { await api.put(`/tm/transactions/${id}`, { notes: form.notes || "" }); toast.success("Notes saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const groups = (txn.tasks || []).reduce((acc, t) => { (acc[t.group_name || "Tasks"] ||= []).push(t); return acc; }, {});
  const total = (txn.tasks || []).length;
  const done = (txn.tasks || []).filter((t) => t.status === "completed").length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const assignedWorkflow = workflows.find((w) => w.template_id === txn.workflow_template_id);
  const datesFilled = KEY_DATES.filter(([k]) => (txn[k] || "").toString().trim()).length;
  const datesPct = KEY_DATES.length ? Math.round((datesFilled / KEY_DATES.length) * 100) : 0;
  const peopleFilled = PARTY_GROUPS.filter(([, base]) => (txn[`${base}_name`] || "").toString().trim()).length;
  const peoplePct = PARTY_GROUPS.length ? Math.round((peopleFilled / PARTY_GROUPS.length) * 100) : 0;
  const readyDone = datesFilled + peopleFilled + done;
  const readyTotal = KEY_DATES.length + PARTY_GROUPS.length + total;
  const readyPct = readyTotal ? Math.round((readyDone / readyTotal) * 100) : 0;
  const propType = (txn.property_type || "").replace(/_/g, " ");
  const sideLabel = txn.transaction_side ? `${txn.transaction_side.charAt(0).toUpperCase()}${txn.transaction_side.slice(1)} Side` : "—";

  return (
    <div data-testid="transaction-detail-page">
      <button data-testid="back-to-transactions" onClick={() => navigate("/transactions")} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> All transactions
      </button>

      <div className="relative mb-8 rounded-2xl overflow-hidden">
        {txn.hero_photo && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img src={txn.hero_photo} alt="" className="w-full h-full object-cover opacity-[0.36]" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/50" />
          </div>
        )}
        <div className="relative flex flex-col lg:flex-row lg:items-start justify-between gap-4 p-1">
          <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-3">
            {editing && canWrite ? (
              <input data-testid="edit-property-address" className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] bg-white border border-[#E5E5E2] rounded-sm px-3 py-2 w-full max-w-xl focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none" value={form.property_address ?? txn.property_address ?? ""} onChange={(e) => set("property_address", e.target.value)} placeholder="Property address" />
            ) : (
              <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A]">{txn.property_address}</h1>
            )}
            {canWrite && !editing && (
              <button data-testid="header-edit-pencil" onClick={() => setEditing(true)} className="text-[#8a8a86] hover:text-[#1F3C88] transition-colors"><Pencil className="w-5 h-5" /></button>
            )}
            <span data-testid="status-pill" className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#1F3C88] text-white text-sm font-semibold capitalize">{(txn.status || "").replace("_", " ")}</span>
          </div>
          <div className="mt-2 text-[#575755] capitalize">{propType || "Property"} <span className="mx-1.5 text-[#C9C9C6]">•</span> Our Rep: {sideLabel}
            {(txn.beds || txn.baths || txn.sqft) && (
              <span data-testid="home-details" className="normal-case"><span className="mx-1.5 text-[#C9C9C6]">•</span>
                {txn.beds ? `${txn.beds} bd` : ""}{txn.beds && (txn.baths || txn.sqft) ? " · " : ""}
                {txn.baths ? `${txn.baths} ba` : ""}{txn.baths && txn.sqft ? " · " : ""}
                {txn.sqft ? `${Number(txn.sqft).toLocaleString()} sqft` : ""}
              </span>
            )}
          </div>
          {txn.last_edited_by && (
            <div data-testid="last-edited-line" className="mt-1.5 text-xs text-[#a3a099]">Last edited by {txn.last_edited_by} · {new Date(txn.last_edited_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canWrite && (
            <button data-testid="notify-parties-button" onClick={notifyParties} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#0A0A0A] hover:bg-[#F0EFEC] transition-colors"><Bell className="w-4 h-4" /> Notify Parties</button>
          )}
          {canWrite && (
            <button data-testid="edit-toggle-button" onClick={() => setEditing((v) => !v)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold transition-colors ${editing ? "bg-[#1F3C88] border-[#1F3C88] text-white" : "border-[#E5E5E2] text-[#0A0A0A] hover:bg-[#F0EFEC]"}`}><Pencil className="w-4 h-4" /> {editing ? "Editing" : "Edit"}</button>
          )}
          {isAdmin && (
            <button data-testid="delete-txn-button" onClick={deleteTxn} className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#E9C9C9] text-[#992B2B] hover:bg-[#FDF3F3] transition-colors"><Trash2 className="w-4 h-4" /></button>
          )}
          </div>
        </div>
      </div>

      {editing && canWrite && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button data-testid="save-txn-button" onClick={save} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312]"><Save className="w-4 h-4" /> Save changes</button>
          <button data-testid="cancel-edit-button" onClick={() => { setEditing(false); load(); }} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#575755] hover:bg-[#F0EFEC]"><X className="w-4 h-4" /> Cancel</button>
          <select data-testid="txn-status-select" value={txn.status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm capitalize focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
            {["pre_listing", "active", "under_contract", "closed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
      )}

      <div data-testid="readiness-card" className="mb-6 bg-white border border-[#E5E5E2] rounded-2xl shadow-sm px-6 py-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-serif text-2xl text-[#0A0A0A]">Transaction Readiness</h3>
          <span data-testid="readiness-pct" className="text-sm font-bold text-[#1F3C88]">{readyPct}%</span>
        </div>
        <div className="h-3 rounded-full bg-[#F0EFEC] overflow-hidden">
          <div className="h-full bg-[#1F8A4C] rounded-full transition-all" data-testid="readiness-bar" style={{ width: `${readyPct}%` }} />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#575755]">
          <span>Key Dates <strong className="text-[#0A0A0A]">{datesFilled}/{KEY_DATES.length}</strong></span>
          <span>People <strong className="text-[#0A0A0A]">{peopleFilled}/{PARTY_GROUPS.length}</strong></span>
          <span>Tasks <strong className="text-[#0A0A0A]">{done}/{total}</strong></span>
        </div>
      </div>

      <Card title="Key Dates" testid="key-dates-card" action={<div className="flex items-center gap-2 flex-wrap justify-end">{datesFilled > 0 && <button type="button" data-testid="invite-all-btn" onClick={() => setInviteKey("all")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E5E2] text-xs font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors"><CalendarDays className="w-3.5 h-3.5" /> Invite All Dates</button>}<button type="button" data-testid="send-invite-btn" onClick={() => setInviteKey("custom")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E5E2] text-xs font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors"><CalendarDays className="w-3.5 h-3.5" /> Send Invite</button><MiniProgress testid="key-dates-progress" filled={datesFilled} count={KEY_DATES.length} pct={datesPct} /></div>}>
        {editing ? (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {KEY_DATES.map(([k, label]) => (
                <div key={k}>
                  <span className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold block mb-1">{DATE_SHORT[k] || label}</span>
                  <DateField testid={`date-${k}`} value={form[k] || ""} onChange={(v) => set(k, v)} />
                </div>
              ))}
              <div>
                <span className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold block mb-1">Expected Days to Close</span>
                <input type="number" min="1" data-testid="field-expected_close_days" className={inp} placeholder="45 (default)" value={form.expected_close_days ?? ""} onChange={(e) => set("expected_close_days", e.target.value)} />
              </div>
            </div>
          </div>
        ) : (
          <>
          {KEY_DATES.some(([k]) => inviteOutdated(txn, k)) && (
            <div data-testid="invite-outdated-banner" className="mb-3 text-xs text-[#9a5b12] bg-[#FFF4E5] border border-[#F7D9A8] rounded-sm px-3 py-2">
              Dates changed after invites were sent: <strong>{KEY_DATES.filter(([k]) => inviteOutdated(txn, k)).map(([k, l]) => DATE_SHORT[k] || l).join(", ")}</strong>. Click Resend under each date to update calendars.
            </div>
          )}
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E5E5E2]">
                  {KEY_DATES.map(([k, label]) => (
                    <th key={k} className="px-3 py-2 text-[11px] tracking-[0.1em] uppercase text-[#8a8a86] font-semibold whitespace-nowrap">{DATE_SHORT[k] || label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {KEY_DATES.map(([k]) => (
                    <td key={k} className="px-3 py-3 whitespace-nowrap">
                      {(txn[k] || "").toString().trim()
                        ? <div><span className="text-[#0A0A0A] font-medium">{fmtDate(txn[k])}</span><div><CalendarInviteButton dateKey={k} txn={txn} onClick={() => setInviteKey(k)} /></div></div>
                        : <span data-testid={`missing-date-${k}`} className="inline-block px-2 py-0.5 rounded-full bg-[#FBEEDD] text-[#B26A00] text-xs font-semibold">Missing</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <InviteHistory txn={txn} />
          </>
        )}
      </Card>

      <Card title="People" testid="people-card" action={
        <div className="flex items-center gap-3">
          <MiniProgress testid="people-progress" filled={peopleFilled} count={PARTY_GROUPS.length} pct={peoplePct} />
          {canWrite && !editing && (
            <button data-testid="people-edit-pencil" onClick={() => setEditing(true)} className="text-[#8a8a86] hover:text-[#1F3C88] transition-colors"><Pencil className="w-4 h-4" /></button>
          )}
        </div>
      }>
        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
            {PARTY_GROUPS.map(([label, base]) => (
              <PartyField key={base} base={base} label={label} form={form} set={set} people={people} onCreatePerson={createPerson} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-5">
            {PARTY_GROUPS.map(([label, base]) => {
              const filled = (txn[`${base}_name`] || "").toString().trim();
              return (
                <div key={base} data-testid={`people-${base}`}>
                  <div className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold">{label}</div>
                  {filled ? (
                    <>
                      <div className="mt-1 text-[#0A0A0A] font-medium">{txn[`${base}_name`]}</div>
                      {txn[`${base}_phone`] && <div className="text-sm text-[#575755]">{txn[`${base}_phone`]}</div>}
                      {txn[`${base}_email`] && <div className="text-sm text-[#8a8a86] truncate">{txn[`${base}_email`]}</div>}
                    </>
                  ) : (
                    <div className="mt-1"><span data-testid={`missing-party-${base}`} className="inline-block px-2 py-0.5 rounded-full bg-[#FBEEDD] text-[#B26A00] text-xs font-semibold">Missing</span></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start mt-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Tasks" testid="tasks-card" action={
            <div className="flex items-center gap-3">
              <span data-testid="tasks-completed-pill" className="px-4 py-1.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#0A0A0A] whitespace-nowrap">{done} / {total} completed</span>
              {canWrite && <button data-testid="add-task-toggle" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#0A0A0A] hover:bg-[#F0EFEC] transition-colors"><Plus className="w-4 h-4" /> Add Task</button>}
            </div>
          }>
            <div className="h-2 rounded-full bg-[#F0EFEC] overflow-hidden mb-5"><div className="h-full bg-[#1F3C88] rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
            {showAdd && canWrite && (
              <form onSubmit={addTask} className="flex items-center gap-2 mb-4">
                <input data-testid="new-task-input" value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add a manual task…" className={inp} autoFocus />
                <button data-testid="add-task-button" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] shrink-0"><Plus className="w-4 h-4" /> Add</button>
              </form>
            )}
            <div className="divide-y divide-[#F0EFEC]">
              {Object.entries(groups).map(([group, tasks]) => (
                <TaskGroup key={group} group={group} tasks={tasks} canWrite={canWrite}
                  toggleTask={toggleTask} openTask={openTask} setOpenTask={setOpenTask}
                  recipients={recipientOptions()} txn={txn} managerEmail={managerEmail} onSave={saveTaskContent} onSend={sendTaskEmail} onResetEmail={resetTaskEmail} />
              ))}
            </div>
            {total === 0 && <div className="text-sm text-[#8a8a86]">No workflow applied yet. Add tasks or apply a workflow below.</div>}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Notes" testid="notes-card">
            <textarea data-testid="field-notes" rows={4} className={inp} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Internal notes…" />
            <div className="mt-2 flex justify-end">
              <button data-testid="save-notes-btn" onClick={saveNotes} className="px-4 py-2 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors">Save Notes</button>
            </div>
          </Card>
          <Card title="Financial" testid="financial-card">
            {editing ? (
              <div className="space-y-4">
                <div><span className={lbl}>Purchase Price</span><input data-testid="field-purchase_price" type="number" className={inp} value={form.purchase_price ?? ""} onChange={(e) => set("purchase_price", e.target.value)} /></div>
                <div><span className={lbl}>Earnest Money</span><input data-testid="field-earnest_money" type="number" className={inp} value={form.earnest_money ?? ""} onChange={(e) => set("earnest_money", e.target.value)} /></div>
              </div>
            ) : (
              <div className="space-y-5">
                <div><div className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold">Purchase Price</div><div className="mt-1 font-serif text-3xl text-[#1F3C88] font-bold">{txn.purchase_price ? currency(txn.purchase_price) : "—"}</div></div>
                <div><div className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold">Earnest Money</div><div className="mt-1 text-lg text-[#0A0A0A] font-medium">{txn.earnest_money ? currency(txn.earnest_money) : "—"}</div></div>
              </div>
            )}
          </Card>

          <Card title="Status" testid="status-card">
            <div className="text-[#0A0A0A]"><span className="text-[#575755]">Current:</span> <span className="font-semibold capitalize">{(txn.status || "").replace("_", " ")}</span></div>
          </Card>

          <Card title="Workflow" testid="workflow-card" action={canWrite && (
            <select data-testid="apply-workflow-select" value={applyId} onChange={(e) => setApplyId(e.target.value)} className="px-2 py-1.5 rounded-sm border border-[#E5E5E2] text-xs max-w-[160px] focus:outline-none">
              <option value="">Choose workflow…</option>
              {workflows.map((w) => <option key={w.template_id} value={w.template_id}>{w.name}</option>)}
            </select>
          )}>
            <div className="mb-4">
              {assignedWorkflow ? (
                <>
                  <div className="text-[11px] tracking-[0.12em] uppercase text-[#8a8a86] font-semibold">Currently assigned</div>
                  <div className="mt-1 font-semibold text-[#0A0A0A]" data-testid="workflow-assigned-name">{assignedWorkflow.name}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-[#F0EFEC] overflow-hidden"><div className="h-full bg-[#1F3C88] rounded-full transition-all" data-testid="workflow-progress-bar" style={{ width: `${pct}%` }} /></div>
                    <span className="text-xs font-semibold text-[#575755] whitespace-nowrap" data-testid="workflow-progress-label">{done}/{total} tasks</span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-[#8a8a86]" data-testid="workflow-none">No workflow assigned yet.</div>
              )}
            </div>
            {canWrite ? (
              <div className="flex flex-wrap gap-2">
                <button data-testid="apply-workflow-replace" onClick={() => applyWorkflow("replace")} className="px-3 py-1.5 rounded-full bg-[#1F3C88] text-white text-xs font-semibold hover:bg-[#152A61]">Replace tasks</button>
                <button data-testid="apply-workflow-append" onClick={() => applyWorkflow("append")} className="px-3 py-1.5 rounded-full border border-[#E5E5E2] text-[#1F3C88] text-xs font-semibold hover:bg-[#F0EFEC]">Add on top</button>
              </div>
            ) : <div className="text-sm text-[#8a8a86]">—</div>}
            <p className="text-xs text-[#a3a099] mt-3">Use "Replace tasks" to switch templates — e.g. move a cancelled deal onto a Cancellation workflow.</p>
          </Card>

          {canWrite && (
            <div className="bg-white border border-[#E5E5E2] rounded-2xl shadow-sm" data-testid="zillow-card">
              <div className="px-6 pt-5 pb-4 flex items-center gap-2.5">
                <Home className="w-5 h-5 text-[#1F3C88]" strokeWidth={1.8} />
                <h3 className="text-sm font-bold tracking-[0.14em] uppercase text-[#0A0A0A]">Zillow Listing</h3>
              </div>
              <div className="px-6 pb-6 border-t border-[#F0EFEC] pt-5">
                <input data-testid="zillow-url-input" value={listingUrl} onChange={(e) => setListingUrl(e.target.value)} placeholder="https://www.zillow.com/homes/..." className={inp} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <button data-testid="zillow-cancel" onClick={() => setListingUrl(txn.listing_url || "")} className="px-4 py-2 rounded-full text-sm font-semibold text-[#575755] hover:bg-[#F0EFEC] transition-colors">Cancel</button>
                  <button data-testid="zillow-save-link" onClick={saveLink} className="px-4 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#0A0A0A] hover:bg-[#F0EFEC] transition-colors">Save Link</button>
                  <button data-testid="zillow-fetch" onClick={fetchListing} disabled={fetchingListing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors disabled:opacity-60">
                    {fetchingListing && <Loader2 className="w-4 h-4 animate-spin" />} Fetch Listing Data
                  </button>
                </div>
                {txn.listing_url && (
                  <a data-testid="zillow-saved-link" href={txn.listing_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#1F3C88] hover:text-[#F7941D] transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Saved listing link</a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title="Documents" testid="documents-card" action={canWrite && (
          <label data-testid="attach-doc-label" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F3C88] cursor-pointer hover:text-[#F7941D] transition-colors">
            {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Attach
            <input data-testid="attach-doc-input" type="file" accept=".pdf,image/*" hidden disabled={uploadingDoc} onChange={uploadDoc} />
          </label>
        )}>
          {(txn.documents || []).length === 0 ? <div className="text-sm text-[#8a8a86]">No documents attached.</div> :
            (txn.documents || []).map((d, i) => (
              <a key={i} data-testid={`doc-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-2 border-b border-[#F5F4F1] last:border-0 text-sm text-[#1F3C88] hover:text-[#F7941D] transition-colors">
                <FileText className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <span className="flex-1 truncate">{d.name || "Contract"}</span>
                {d.kind && <span className="text-[10px] uppercase tracking-wide text-[#8a8a86]">{d.kind}</span>}
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </a>
            ))}
        </Card>

        <Card title="Activity Log" testid="activity-card">
          {(txn.activity || []).length === 0 ? <div className="text-sm text-[#8a8a86]">No activity recorded yet.</div> :
            <div data-testid="activity-scroll" className="max-h-96 overflow-y-auto pr-1 -mr-1">
            {[...(txn.activity || [])].reverse().map((a, i) => (
              <div key={i} data-testid={`activity-${i}`} className="flex items-start gap-3 py-2 border-b border-[#F5F4F1] last:border-0">
                <History className="w-4 h-4 text-[#8a8a86] shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A0A0A]">{a.action}</div>
                  <div className="text-xs text-[#a3a099]">{a.by} · {new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                </div>
              </div>
            ))}
            </div>}
        </Card>
      </div>

      <CalendarInviteDialog txn={txn} dateKey={inviteKey} label={inviteKey === "custom" ? "Custom event" : inviteKey === "all" ? "All dates" : (KEY_DATES.find(([k]) => k === inviteKey) || [])[1]} sender={user} onClose={() => setInviteKey(null)} onSent={load} />
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="notify-modal">
          <div className="flex items-start gap-3">
            <Bell className="w-6 h-6 text-[#1F3C88] mt-0.5" strokeWidth={1.8} />
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#0A0A0A]">Notify Parties — Deadline Update</h2>
              <p className="text-sm text-[#575755] mt-1">Review and send a deadline update notification to selected parties.</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#8a8a86] mb-2">Recipients</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {notifyTo.map((email) => (
                <span key={email} data-testid={`recipient-chip-${email}`} className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-[#F0EFEC] text-sm font-medium text-[#0A0A0A]">
                  {email}
                  <button data-testid={`remove-recipient-${email}`} onClick={() => setNotifyTo(notifyTo.filter((e) => e !== email))} className="text-[#8a8a86] hover:text-[#992B2B]"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
              {notifyTo.length === 0 && <span className="text-sm text-[#a3a099]">No recipients — add one below.</span>}
            </div>
            <input data-testid="add-recipient-input" value={notifyInput} onChange={(e) => setNotifyInput(e.target.value)} onKeyDown={addRecipient} onBlur={addRecipient} placeholder="Add email…" className={inp} />
          </div>

          <div className="mt-4">
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#8a8a86] mb-2">Subject</div>
            <input data-testid="notify-subject" value={notifySubject} onChange={(e) => setNotifySubject(e.target.value)} className={inp} />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#8a8a86]">Email Body</div>
              <button data-testid="notify-body-edit-toggle" onClick={() => setNotifyEditBody((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F3C88] hover:text-[#F7941D] transition-colors"><Pencil className="w-4 h-4" /> {notifyEditBody ? "Preview" : "Edit"}</button>
            </div>
            {notifyEditBody
              ? <RichTextEditor testid="notify-body-editor" value={notifyBody} onChange={setNotifyBody} placeholder="Email body…" />
              : <div data-testid="notify-body-preview" className="border border-[#E5E5E2] rounded-xl p-5 text-sm text-[#0A0A0A]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notifyBody) }} />}
          </div>

          <div className="flex items-center justify-end gap-2 mt-6">
            <button data-testid="notify-cancel" onClick={() => setNotifyOpen(false)} className="px-5 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#575755] hover:bg-[#F0EFEC] transition-colors">Cancel</button>
            <button data-testid="notify-send" onClick={sendNotify} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors"><Send className="w-4 h-4" /> Send Notification</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskGroup({ group, tasks, canWrite, toggleTask, openTask, setOpenTask, recipients, txn, managerEmail, onSave, onSend, onResetEmail }) {
  const [open, setOpen] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const doneTasks = tasks.filter((t) => t.status === "completed");
  const pending = tasks.filter((t) => t.status !== "completed");
  const slug = group.replace(/\s+/g, "-").toLowerCase();
  const row = (t) => {
    const dt = daysTo(t.due_date);
    const overdue = t.status !== "completed" && dt != null && dt < 0;
    const isOpen = openTask === t.task_id;
    return (
      <div key={t.task_id} data-testid={`task-${t.task_id}`} className="border-b border-[#F5F4F1] last:border-0">
        <div className="flex items-center gap-3 py-2.5">
          <button disabled={!canWrite} data-testid={`toggle-task-${t.task_id}`} onClick={() => toggleTask(t)}>
            {t.status === "completed" ? <CheckCircle2 className="w-5 h-5 text-[#2E5C47]" strokeWidth={1.5} /> : <Circle className="w-5 h-5 text-[#B9B5AC] hover:text-[#F7941D]" strokeWidth={1.5} />}
          </button>
          <div className="flex-1 min-w-0">
            <div className={`text-sm flex items-center gap-1.5 ${t.status === "completed" ? "text-[#a3a099] line-through" : "text-[#0A0A0A]"}`}>
              {t.is_key_date && <Star className="w-3.5 h-3.5 text-[#F7941D] fill-[#F7941D]" />}
              {t.is_email && <Mail className="w-3.5 h-3.5 text-[#1F3C88]" />}
              {t.title}
            </div>
            {t.last_sent_at && <div className="text-[10px] text-[#2E5C47]">Sent to {t.last_sent_to}</div>}
            {t.auto_sent_at && (
              <div data-testid={`auto-sent-${t.task_id}`} className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#E7F0EA] text-[#2E5C47] text-[10px] font-semibold">
                <Send className="w-2.5 h-2.5" /> Auto-sent {fmtSentAt(t.auto_sent_at)}
              </div>
            )}
            {t.is_email && t.email_edited && (
              <div data-testid={`email-edited-${t.task_id}`} className="mt-0.5 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FDF0E1] text-[#B4690E] text-[10px] font-semibold" title="This email was manually edited and differs from the workflow template">
                <Pencil className="w-2.5 h-2.5" /> Edited
              </div>
            )}
          </div>
          <div className="shrink-0">
            {canWrite ? (
              <input
                type="date"
                data-testid={`due-date-${t.task_id}`}
                value={t.due_date || ""}
                onChange={(e) => onSave(t, { due_date: e.target.value })}
                title={overdue ? "Overdue" : "Due date"}
                className={`text-xs px-2 py-1 rounded border bg-white focus:outline-none focus:ring-1 focus:ring-[#F7941D] transition-colors duration-200 ${overdue ? "border-[#992B2B] text-[#992B2B] font-semibold" : "border-[#E5E5E2] text-[#575755]"}`}
              />
            ) : (
              <span className={`text-xs ${overdue ? "text-[#992B2B] font-semibold" : "text-[#575755]"}`}>{t.due_date || "—"}{overdue && " · overdue"}</span>
            )}
          </div>
          {canWrite && (t.is_email || t.description) && (
            <button data-testid={`expand-task-${t.task_id}`} onClick={() => setOpenTask(isOpen ? null : t.task_id)} className="text-[#8a8a86] hover:text-[#1F3C88]"><ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></button>
          )}
        </div>
        {isOpen && canWrite && <TaskEditor t={t} recipients={recipients} txn={txn} managerEmail={managerEmail} onSave={onSave} onSend={onSend} onResetEmail={onResetEmail} />}
      </div>
    );
  };
  return (
    <div className="py-1">
      <button onClick={() => setOpen((v) => !v)} data-testid={`group-toggle-${slug}`} className="w-full flex items-center justify-between py-3">
        <span className="font-semibold text-[#0A0A0A]">{group}</span>
        <span className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full border border-[#E5E5E2] text-xs font-semibold text-[#575755]">{doneTasks.length}/{tasks.length}</span>
          <ChevronDown className={`w-4 h-4 text-[#8a8a86] transition-transform ${open ? "" : "-rotate-90"}`} />
        </span>
      </button>
      {open && (
        <div className="pb-2">
          {pending.length === 0 && <div className="text-sm text-[#2E5C47] font-medium text-center py-3">All tasks in this group are completed</div>}
          {pending.map(row)}
          {doneTasks.length > 0 && (
            <div className="mt-2 rounded-lg bg-[#F3F8F4] border border-[#DCEBE1] overflow-hidden">
              <button onClick={() => setShowDone((v) => !v)} data-testid={`completed-toggle-${slug}`} className="w-full flex items-center justify-between px-3 py-2.5">
                <span className="flex items-center gap-2 text-sm font-semibold text-[#2E5C47]"><CheckCircle2 className="w-4 h-4" /> Completed ({doneTasks.length})</span>
                <ChevronDown className={`w-4 h-4 text-[#2E5C47] transition-transform ${showDone ? "rotate-180" : ""}`} />
              </button>
              {showDone && <div className="px-3 pb-1 bg-white/60">{doneTasks.map(row)}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskEditor({ t, recipients, txn, managerEmail, onSave, onSend, onResetEmail }) {
  const [notes, setNotes] = useState(t.description || "");
  const [subject, setSubject] = useState(t.email_subject || "");
  const [body, setBody] = useState(t.email_body || "");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const inpc = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
  const lblc = "text-[11px] tracking-[0.1em] uppercase text-[#8a8a86] block mb-1";
  return (
    <div data-testid={`task-editor-${t.task_id}`} className="pb-4 pl-8 pr-1 space-y-3">
      <div>
        <span className={lblc}>Notes / Instructions</span>
        <RichTextEditor testid={`task-notes-${t.task_id}`} value={notes} onChange={setNotes} placeholder="Notes for this task…" />
      </div>
      {t.is_email && (
        <div className="space-y-2 border-l-2 border-[#1F3C88] pl-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><span className={lblc}>To <span className="text-[#992B2B]">*</span></span><EmailField testid={`task-to-${t.task_id}`} value={to} onChange={setTo} options={recipients} /><QuickPickChips recipients={recipients} value={to} onChange={setTo} testid={`task-to-${t.task_id}`} /></div>
            <div><span className={lblc}>Cc</span><EmailField testid={`task-cc-${t.task_id}`} value={cc} onChange={setCc} options={recipients} /></div>
            <div><span className={lblc}>Bcc</span><EmailField testid={`task-bcc-${t.task_id}`} value={bcc} onChange={setBcc} options={recipients} /></div>
          </div>
          <div><span className={lblc}>Email Subject</span>
            <input data-testid={`task-subject-${t.task_id}`} className={inpc} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" /></div>
          <div><span className={lblc}>Email Body</span>
            <RichTextEditor testid={`task-body-${t.task_id}`} value={body} onChange={setBody} placeholder="Email body…" mergeFields /></div>
          {t.email_edited && (
            <div className="flex items-center justify-between gap-2 bg-[#FDF0E1] rounded-sm px-3 py-2">
              <span className="text-[11px] text-[#B4690E] flex items-center gap-1.5"><Pencil className="w-3 h-3" /> This email was manually edited — it won't auto-update from the template.</span>
              <button type="button" data-testid={`reset-email-${t.task_id}`} onClick={() => onResetEmail(t)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1F3C88] hover:text-[#152A61]"><RotateCcw className="w-3 h-3" /> Reset to template</button>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid={`save-task-content-${t.task_id}`} onClick={() => onSave(t, { description: notes, email_subject: subject, email_body: body })} className="px-3 py-1.5 rounded-full bg-[#1F3C88] text-white text-xs font-semibold hover:bg-[#152A61]">Save changes</button>
        {t.is_email && (
          <button data-testid={`send-task-${t.task_id}`} disabled={!to.trim()} title={!to.trim() ? "Select or add a recipient first" : undefined} onClick={() => onSend(t, { to, cc, bcc })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F7941D] text-white text-xs font-semibold hover:bg-[#E08312] disabled:opacity-50 disabled:cursor-not-allowed"><Send className="w-3.5 h-3.5" /> Send now</button>
        )}
        {t.is_email && !to.trim() && <span data-testid={`task-to-required-${t.task_id}`} className="text-[11px] text-[#992B2B]">Select or add a recipient in To before sending.</span>}
      </div>
      <p className="text-[11px] text-[#a3a099]">Review and edit before sending — tokens like {"{{buyer_name}}"} are already filled from this transaction. Pick a contact or type any email in To / Cc / Bcc.</p>
    </div>
  );
}
